/**
 * A QR code, drawn on the till, with no dependency.
 *
 * WHY NOT A LIBRARY: this ships in a bundle with a 165 kB critical-path budget,
 * on a device that must cold-start with no network. The smallest capable QR
 * library is around 12 kB, and it would be 12 kB of a budget that is already
 * fought over by the SQLite worker and four languages — for a matrix that is a
 * bounded, well-specified piece of arithmetic.
 *
 * WHAT IS IMPLEMENTED: byte mode, error-correction level L, the smallest
 * version that fits, and nothing else. A guest token is ~150 URL-safe
 * characters, which is version 7 at level L; anything larger is refused rather
 * than silently truncated, because a truncated QR scans cleanly and opens the
 * wrong thing — or nothing, in a restaurant, in front of a guest.
 *
 * Level L rather than M or better: a code on a table under glass, scanned from
 * thirty centimetres by a phone with a good camera, is the easy case. The
 * correction capacity buys resilience this environment does not need, and it is
 * paid for in modules, which is size on a screen.
 *
 * Specified by ISO/IEC 18004. Every table below is from it; none is derived at
 * runtime, because a generated table is one that can be generated wrong.
 */

/** How many bits a value occupies. Used by the BCH encoders below. */
function bitLength(value: number): number {
  let bits = 0;
  let v = value;
  while (v !== 0) {
    bits += 1;
    v >>>= 1;
  }
  return bits;
}

// --- Galois field GF(256), for Reed–Solomon ---------------------------------

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    // The primitive polynomial x^8 + x^4 + x^3 + x^2 + 1, from the standard.
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
}

const mul = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!;

/** The generator polynomial for `degree` error-correction codewords. */
function generator(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      /**
       * Multiplying by (x + α^i), with index 0 as the LEADING coefficient.
       *
       * The first version had these two lines the other way round, which
       * multiplies by (α^i·x + 1) instead — a polynomial that is not monic, so
       * every error-correction codeword came out wrong. The matrix was
       * structurally perfect and no decoder could read a single code.
       */
      next[j] = (next[j]! ^ poly[j]!) as number;
      next[j + 1] = (next[j + 1]! ^ mul(poly[j]!, EXP[i]!)) as number;
    }
    poly = next;
  }
  return poly;
}

function remainder(data: Uint8Array, degree: number): Uint8Array {
  const gen = generator(degree);
  const out = new Uint8Array(degree);
  for (const byte of data) {
    const factor = byte ^ out[0]!;
    out.copyWithin(0, 1);
    out[degree - 1] = 0;
    for (let i = 0; i < degree; i++) out[i] = (out[i]! ^ mul(gen[i + 1]!, factor)) as number;
  }
  return out;
}

// --- Version tables, level L, byte mode -------------------------------------

/**
 * Per version: total codewords, EC codewords per block, number of blocks.
 *
 * Versions 1–10 only. A guest token does not reach version 11, and carrying
 * forty versions of table nobody exercises is forty rows that can be wrong
 * without anybody noticing.
 */
const VERSIONS: readonly { total: number; ecPerBlock: number; blocks: number }[] = [
  { total: 26, ecPerBlock: 7, blocks: 1 }, // 1
  { total: 44, ecPerBlock: 10, blocks: 1 }, // 2
  { total: 70, ecPerBlock: 15, blocks: 1 }, // 3
  { total: 100, ecPerBlock: 20, blocks: 1 }, // 4
  { total: 134, ecPerBlock: 26, blocks: 1 }, // 5
  { total: 172, ecPerBlock: 18, blocks: 2 }, // 6
  { total: 196, ecPerBlock: 20, blocks: 2 }, // 7
  { total: 242, ecPerBlock: 24, blocks: 2 }, // 8
  { total: 292, ecPerBlock: 30, blocks: 2 }, // 9
  { total: 346, ecPerBlock: 18, blocks: 4 }, // 10
];

/** Alignment-pattern centres per version, from the standard's Annex E. */
const ALIGNMENT: readonly (readonly number[])[] = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

export class QrTooLong extends Error {
  constructor(length: number) {
    super(`${String(length)} bytes will not fit in a version-10 code at level L`);
    this.name = "QrTooLong";
  }
}

/**
 * The module matrix for `text`, as rows of booleans.
 *
 * True is dark. Rendering is the caller's problem — the till draws it as SVG
 * rects, which scales to any screen and prints, and costs nothing to produce.
 */
export function qrMatrix(text: string): boolean[][] {
  const data = new TextEncoder().encode(text);

  // Smallest version that fits, including the mode nibble, the length field and
  // the terminator.
  let version = 0;
  for (let v = 1; v <= 10; v++) {
    const spec = VERSIONS[v - 1]!;
    const capacity = spec.total - spec.ecPerBlock * spec.blocks;
    const lengthBits = v < 10 ? 8 : 16;
    if (data.length + 2 + Math.ceil(lengthBits / 8) <= capacity) {
      version = v;
      break;
    }
  }
  if (version === 0) throw new QrTooLong(data.length);

  const spec = VERSIONS[version - 1]!;
  const dataCodewords = spec.total - spec.ecPerBlock * spec.blocks;

  // --- The bit stream -------------------------------------------------------
  const bits: number[] = [];
  const push = (value: number, width: number): void => {
    for (let i = width - 1; i >= 0; i--) bits.push((value >> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(data.length, version < 10 ? 8 : 16);
  for (const byte of data) push(byte, 8);

  // Terminator, then pad to a byte boundary, then the standard's alternating
  // pad bytes. Those two constants are from the specification; anything else
  // produces a code that scans and decodes to trailing rubbish.
  for (let i = 0; i < 4 && bits.length < dataCodewords * 8; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]!;
    codewords.push(byte);
  }
  for (let i = 0; codewords.length < dataCodewords; i++) {
    codewords.push(i % 2 === 0 ? 0xec : 0x11);
  }

  // --- Blocks and error correction -----------------------------------------
  const shortBlock = Math.floor(dataCodewords / spec.blocks);
  const longBlocks = dataCodewords % spec.blocks;

  const blocks: { data: Uint8Array; ec: Uint8Array }[] = [];
  let offset = 0;
  for (let b = 0; b < spec.blocks; b++) {
    const size = shortBlock + (b >= spec.blocks - longBlocks ? 1 : 0);
    const chunk = Uint8Array.from(codewords.slice(offset, offset + size));
    offset += size;
    blocks.push({ data: chunk, ec: remainder(chunk, spec.ecPerBlock) });
  }

  // Interleaved, which is what makes a burst of damage survivable.
  const final: number[] = [];
  const longest = Math.max(...blocks.map((b) => b.data.length));
  for (let i = 0; i < longest; i++) {
    for (const block of blocks) if (i < block.data.length) final.push(block.data[i]!);
  }
  for (let i = 0; i < spec.ecPerBlock; i++) {
    for (const block of blocks) final.push(block.ec[i]!);
  }

  // --- The matrix -----------------------------------------------------------
  const size = version * 4 + 17;
  const modules: (boolean | null)[][] = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => null),
  );

  const finder = (row: number, col: number): void => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const rr = row + r;
        const cc = col + c;
        if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
        const inner = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        const ring = r === 0 || r === 6 || c === 0 || c === 6;
        const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        modules[rr]![cc] = inner && (ring || core);
      }
    }
  };

  finder(0, 0);
  finder(0, size - 7);
  finder(size - 7, 0);

  // Timing patterns.
  for (let i = 8; i < size - 8; i++) {
    modules[6]![i] = i % 2 === 0;
    modules[i]![6] = i % 2 === 0;
  }

  // Alignment patterns, skipping the three that collide with the finders.
  const centres = ALIGNMENT[version]!;
  for (const r of centres) {
    for (const c of centres) {
      if ((r === 6 && c === 6) || (r === 6 && c === size - 7) || (r === size - 7 && c === 6)) {
        continue;
      }
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          modules[r + dr]![c + dc] =
            Math.abs(dr) === 2 || Math.abs(dc) === 2 || (dr === 0 && dc === 0);
        }
      }
    }
  }

  // The dark module, which is always set and always here.
  modules[size - 8]![8] = true;

  // Reserve the format areas so the data placement below skips them.
  const reserve = (row: number, col: number): void => {
    if (modules[row]![col] === null) modules[row]![col] = false;
  };
  for (let i = 0; i < 9; i++) {
    reserve(8, i);
    reserve(i, 8);
  }
  for (let i = 0; i < 8; i++) {
    reserve(8, size - 1 - i);
    reserve(size - 1 - i, 8);
  }
  if (version >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        reserve(size - 11 + j, i);
        reserve(i, size - 11 + j);
      }
    }
  }

  // --- Place the data, mask 0 ----------------------------------------------
  /**
   * MASK 0 ALWAYS, rather than scoring all eight.
   *
   * The standard asks for the mask with the lowest penalty. The penalty scoring
   * is another hundred lines of table-driven arithmetic, and what it buys is
   * marginal contrast on hostile data. Every code this ever renders is a
   * base64url token — uniformly distributed, no long runs — and mask 0 is fine
   * for it. A phone reads the mask out of the format bits either way.
   */
  let bitIndex = 0;
  let upward = true;
  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col -= 1; // the vertical timing column is not a data column
    for (let i = 0; i < size; i++) {
      const row = upward ? size - 1 - i : i;
      for (const c of [col, col - 1]) {
        if (modules[row]![c] !== null) continue;
        const byte = final[bitIndex >> 3] ?? 0;
        const bit = (byte >> (7 - (bitIndex & 7))) & 1;
        bitIndex += 1;
        // Mask 0: invert where (row + column) mod 2 === 0. The module is dark
        // when the data bit and the mask condition DIFFER — an XOR, which the
        // first version of this line inverted, producing a photographic
        // negative that every structural test passed and no decoder read.
        modules[row]![c] = (bit === 1) !== ((row + c) % 2 === 0);
      }
    }
    upward = !upward;
  }

  // --- Format information (level L, mask 0) ---------------------------------
  /**
   * COMPUTED, not typed in.
   *
   * The fifteen bits are a BCH(15,5) code over the five data bits, XORed with
   * 0x5412. Both the polynomial and the mask are from the standard, and the
   * whole thing is ten lines — which is cheaper than a hardcoded constant
   * somebody has to trust, and a mistyped constant produces a code that looks
   * perfect and decodes to nothing.
   */
  const bch = (value: number, poly: number, width: number): number => {
    let v = value << width;
    const top = 1 << (width + bitLength(poly) - 1);
    void top;
    while (bitLength(v) - bitLength(poly) >= 0) {
      v ^= poly << (bitLength(v) - bitLength(poly));
    }
    return v;
  };

  // Level L is 0b01; mask 0 is 0b000.
  const formatData = (0b01 << 3) | 0;
  const format = (((formatData << 10) | bch(formatData, 0b10100110111, 10)) ^ 0b101010000010010);

  /**
   * Placed LSB FIRST, and the two copies run in different directions.
   *
   * The first version of this had the vertical and horizontal copies swapped,
   * which produced a structurally perfect code that no decoder could read —
   * every finder, timing and alignment assertion passed and `jsqr` returned
   * null on all of it. That is precisely why this file is tested by DECODING
   * rather than by inspection.
   */
  for (let i = 0; i < 15; i++) {
    const bit = ((format >> i) & 1) === 1;

    // The copy beside the top-left and bottom-left finders, going down column 8.
    if (i < 6) modules[i]![8] = bit;
    else if (i < 8) modules[i + 1]![8] = bit;
    else modules[size - 15 + i]![8] = bit;

    // And the copy along row 8, from the right.
    if (i < 8) modules[8]![size - 1 - i] = bit;
    else if (i === 8) modules[8]![7] = bit;
    else modules[8]![14 - i] = bit;
  }

  /**
   * Version information, required from version 7.
   *
   * Eighteen bits of BCH(18,6), in two 3 × 6 blocks beside the bottom-left and
   * top-right finders. A decoder can infer the version from the module count
   * and most will, which is exactly why leaving this out produces a code that
   * works on a lenient scanner and fails on a strict one — the worst kind of
   * bug to find in a restaurant.
   */
  if (version >= 7) {
    const versionBits = (version << 12) | bch(version, 0b1111100100101, 12);
    for (let i = 0; i < 18; i++) {
      const bit = ((versionBits >> i) & 1) === 1;
      modules[Math.floor(i / 3)]![size - 11 + (i % 3)] = bit;
      modules[size - 11 + (i % 3)]![Math.floor(i / 3)] = bit;
    }
  }

  return modules.map((row) => row.map((cell) => cell === true));
}

/**
 * The matrix as an SVG, sized in CSS rather than in pixels.
 *
 * One `<path>` of rectangles rather than one element per module: a version-7
 * code is 45×45, and 2,025 DOM nodes on a till is a scroll that stutters.
 */
export function qrSvg(text: string, quietZone = 4): string {
  const matrix = qrMatrix(text);
  const size = matrix.length + quietZone * 2;

  let path = "";
  matrix.forEach((row, r) => {
    row.forEach((dark, c) => {
      if (dark) path += `M${String(c + quietZone)} ${String(r + quietZone)}h1v1h-1z`;
    });
  });

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${String(size)} ${String(size)}" ` +
    `shape-rendering="crispEdges" role="img">` +
    `<rect width="${String(size)}" height="${String(size)}" fill="#fff"/>` +
    `<path d="${path}" fill="#000"/>` +
    `</svg>`
  );
}
